import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faCrop, faDownload, faEye, faFilePdf, faImage, faSpinner, faVideo } from '@fortawesome/free-solid-svg-icons';
import BoardResultsPosterCard from './BoardResultsPosterCard';
import BoardResultsPosterPreview from './BoardResultsPosterPreview';
import BoardResultsTop5Poster from './BoardResultsTop5Poster';
import BoardResultsFlyerPoster, {
  FLYER_MIN_MARKS,
  FLYER_THEMES,
  FLYER_WIDTH_PX,
  loadFlyerPhotoPlacements,
  loadFlyerTheme,
  saveFlyerPhotoPlacements,
  saveFlyerTheme
} from './BoardResultsFlyerPoster';
import BoardResultsFlyerPhotoEditor from './BoardResultsFlyerPhotoEditor';
import BoardResultsPosterVideo from './BoardResultsPosterVideo';
import {
  capturePosterElement,
  downloadBlob,
  exportFlyerPdf,
  fetchPosterPhotosParallel,
  flyerPdfFilename,
  flyerPosterFilename,
  posterFilename,
  revokePhotoUrl,
  top12PosterFilename,
  top20PosterFilename,
  top5PosterFilename,
  uploadPosterPhoto
} from '../utils/exportBoardResultsPosters';
import './BoardResultsPosters.css';

const MIN_PCT = 75;
const SEL_TOP5 = 'top5';
const SEL_TOP12 = 'top12';
const SEL_TOP20 = 'top20';
const SEL_FLYER = 'flyer80';
const studentSelKey = (id) => `s:${id}`;

function pickTopN(rows, n) {
  const enabled = rows.filter((r) => !r.is_disabled);
  const ranked = enabled
    .filter((r) => {
      const num = Number(r.rank_no);
      return Number.isFinite(num) && num >= 1 && num <= n;
    })
    .sort((a, b) => Number(a.rank_no) - Number(b.rank_no));
  if (ranked.length) return ranked.slice(0, n);
  return [...enabled]
    .sort((a, b) => (rowPct(b) || 0) - (rowPct(a) || 0))
    .slice(0, n);
}

function rowPct(row) {
  if (row?.percentage != null && Number.isFinite(Number(row.percentage))) {
    return Number(row.percentage);
  }
  const label = String(row?.percentage_label || '').replace('%', '').trim();
  const n = Number(label);
  return Number.isFinite(n) ? n : null;
}

export default function BoardResultsPosters({
  rows,
  listLoading,
  level,
  part,
  year,
  groupLabel,
  sectionLabel,
  apiBaseUrl,
  authHeaders
}) {
  const posterRefs = useRef(new Map());
  const photoUrlsRef = useRef({});
  const [photoUrls, setPhotoUrls] = useState({});
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photoLoadProgress, setPhotoLoadProgress] = useState({ done: 0, total: 0 });
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewTop5, setPreviewTop5] = useState(false);
  const [previewTop12, setPreviewTop12] = useState(false);
  const [previewTop20, setPreviewTop20] = useState(false);
  const [downloadingTop5, setDownloadingTop5] = useState(false);
  const [downloadingTop12, setDownloadingTop12] = useState(false);
  const [downloadingTop20, setDownloadingTop20] = useState(false);
  const [downloadingFlyer, setDownloadingFlyer] = useState(false);
  const [downloadingFlyerPdf, setDownloadingFlyerPdf] = useState(false);
  const top5Ref = useRef(null);
  const top12Ref = useRef(null);
  const top20Ref = useRef(null);
  const flyerRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);
  const replaceFileRef = useRef(null);
  const replaceRowRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [previewFlyer, setPreviewFlyer] = useState(false);
  const [flyerTheme, setFlyerTheme] = useState(loadFlyerTheme);
  const [flyerThumbScale, setFlyerThumbScale] = useState(0.25);
  const flyerThumbFrameRef = useRef(null);
  const [adjustingFlyerPhotos, setAdjustingFlyerPhotos] = useState(false);
  const [photoPlacements, setPhotoPlacements] = useState(loadFlyerPhotoPlacements);
  const [editingPhotoRow, setEditingPhotoRow] = useState(null);

  const setFlyerThemeAndSave = useCallback((key) => {
    setFlyerTheme(key);
    saveFlyerTheme(key);
  }, []);

  const handleFlyerPhotoClick = useCallback((row) => {
    if (!row?.id) return;
    setEditingPhotoRow(row);
  }, []);

  const handleSavePhotoPlacement = useCallback((placement) => {
    if (!editingPhotoRow?.id) return;
    setPhotoPlacements((prev) => {
      const next = { ...prev, [editingPhotoRow.id]: placement };
      saveFlyerPhotoPlacements(next);
      return next;
    });
  }, [editingPhotoRow]);

  const handleFlyerPlacementChange = useCallback((row, placement) => {
    if (!row?.id) return;
    setPhotoPlacements((prev) => {
      const next = { ...prev, [row.id]: placement };
      saveFlyerPhotoPlacements(next);
      return next;
    });
  }, []);

  const getFlyerCaptureElement = useCallback(() => flyerRef.current, []);

  const candidates = React.useMemo(() => {
    return rows
      .filter((r) => {
        if (r.is_disabled) return false;
        const pct = rowPct(r);
        return pct != null && pct >= MIN_PCT;
      })
      .sort((a, b) => {
        const ra = a.rank_no != null ? Number(a.rank_no) : 9999;
        const rb = b.rank_no != null ? Number(b.rank_no) : 9999;
        if (ra !== rb) return ra - rb;
        return rowPct(b) - rowPct(a);
      });
  }, [rows]);

  const top5 = React.useMemo(() => pickTopN(rows, 5), [rows]);
  const top12 = React.useMemo(() => pickTopN(rows, 12), [rows]);
  const top20 = React.useMemo(() => pickTopN(rows, 20), [rows]);
  const flyer80 = React.useMemo(() => {
    return rows
      .filter((r) => {
        if (r.is_disabled) return false;
        const marks = Number(r.total_marks);
        return Number.isFinite(marks) && marks >= FLYER_MIN_MARKS;
      })
      .sort((a, b) => {
        // Same order as ranks / individual posters: percentage first
        const pa = rowPct(a) || 0;
        const pb = rowPct(b) || 0;
        if (pb !== pa) return pb - pa;
        const mb = Number(b.total_marks) || 0;
        const ma = Number(a.total_marks) || 0;
        if (mb !== ma) return mb - ma;
        const ra = a.rank_no != null ? Number(a.rank_no) : 9999;
        const rb = b.rank_no != null ? Number(b.rank_no) : 9999;
        return ra - rb;
      });
  }, [rows]);

  useEffect(() => {
    const frame = flyerThumbFrameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      if (w > 0) setFlyerThumbScale(w / FLYER_WIDTH_PX);
    });
    ro.observe(frame);
    setFlyerThumbScale(frame.clientWidth / FLYER_WIDTH_PX);
    return () => ro.disconnect();
  }, [flyer80.length]);

  const allSelectableKeys = React.useMemo(() => {
    const keys = [];
    if (flyer80.length) keys.push(SEL_FLYER);
    if (top5.length) keys.push(SEL_TOP5);
    if (top12.length) keys.push(SEL_TOP12);
    if (top20.length) keys.push(SEL_TOP20);
    for (const row of candidates) {
      if (row?.id != null) keys.push(studentSelKey(row.id));
    }
    return keys;
  }, [candidates, flyer80, top5, top12, top20]);

  // Drop selection for posters that left the current view
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(allSelectableKeys);
      let changed = false;
      const next = new Set();
      for (const k of prev) {
        if (allowed.has(k)) next.add(k);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [allSelectableKeys]);

  const selectedCount = selectedKeys.size;
  const allSelected = allSelectableKeys.length > 0
    && allSelectableKeys.every((k) => selectedKeys.has(k));

  const toggleSelected = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAllPosters = useCallback(() => {
    setSelectedKeys(new Set(allSelectableKeys));
  }, [allSelectableKeys]);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  photoUrlsRef.current = photoUrls;

  // Load photos in parallel; show each poster as its photo arrives.
  useEffect(() => {
    let cancelled = false;

    const photoRows = [...candidates, ...top5, ...top12, ...top20, ...flyer80].filter((row, i, all) => (
      row?.id && all.findIndex((r) => r.id === row.id) === i
    ));
    const neededIds = photoRows.map((row) => row.id).filter(Boolean);

    setPhotoUrls((prev) => {
      const needed = new Set(neededIds);
      const next = { ...prev };
      let changed = false;
      for (const [id, url] of Object.entries(prev)) {
        if (!needed.has(id)) {
          revokePhotoUrl(url);
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    const toFetch = neededIds.filter((id) => !photoUrlsRef.current[id]);
    if (!toFetch.length || !apiBaseUrl) {
      setPhotosLoading(false);
      setPhotoLoadProgress({ done: 0, total: 0 });
      return () => {
        cancelled = true;
      };
    }

    let loaded = 0;
    setPhotosLoading(true);
    setPhotoLoadProgress({ done: 0, total: toFetch.length });

    fetchPosterPhotosParallel(apiBaseUrl, authHeaders, toFetch, {
      concurrency: 8,
      onPhoto: (rowId, url) => {
        if (cancelled) {
          revokePhotoUrl(url);
          return;
        }
        loaded += 1;
        setPhotoUrls((prev) => ({ ...prev, [rowId]: url }));
        setPhotoLoadProgress({ done: loaded, total: toFetch.length });
      }
    }).finally(() => {
      if (!cancelled) {
        setPhotosLoading(false);
        setPhotoLoadProgress({ done: 0, total: 0 });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [candidates, top5, top12, top20, flyer80, apiBaseUrl, authHeaders]);

  useEffect(() => () => {
    Object.values(photoUrlsRef.current).forEach(revokePhotoUrl);
  }, []);

  const setPosterRef = useCallback((id, el) => {
    if (el) posterRefs.current.set(id, el);
    else posterRefs.current.delete(id);
  }, []);

  const handleDownloadOne = async (row) => {
    const el = posterRefs.current.get(row.id);
    if (!el) return;
    setDownloadingId(row.id);
    try {
      const blob = await capturePosterElement(el);
      downloadBlob(blob, posterFilename(row));
    } catch (err) {
      console.error('[BoardResultsPosters] download one', err);
      alert(`Could not save poster: ${err.message || err}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePhotoReplaced = useCallback((rowId, url) => {
    if (!rowId || !url) return;
    setPhotoUrls((prev) => {
      const old = prev[rowId];
      if (old && old !== url) revokePhotoUrl(old);
      return { ...prev, [rowId]: url };
    });
  }, []);

  const handleReplacePhotoPick = (row) => {
    replaceRowRef.current = row;
    replaceFileRef.current?.click();
  };

  const handleReplacePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const row = replaceRowRef.current;
    replaceRowRef.current = null;
    if (!file || !row?.id) return;
    setReplacingId(row.id);
    try {
      const url = await uploadPosterPhoto(apiBaseUrl, authHeaders, row.id, file);
      if (!url) throw new Error('Photo saved but could not reload');
      handlePhotoReplaced(row.id, url);
    } catch (err) {
      alert(err.message || 'Could not replace photo');
    } finally {
      setReplacingId(null);
    }
  };

  const top5Meta = [sectionLabel, groupLabel, [level, part, year].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');

  const handleDownloadTop5 = async () => {
    const el = top5Ref.current;
    if (!el) return;
    setDownloadingTop5(true);
    try {
      const blob = await capturePosterElement(el);
      downloadBlob(blob, top5PosterFilename({ level, part, year, groupLabel }));
    } catch (err) {
      alert(`Could not save Top 5 poster: ${err.message || err}`);
    } finally {
      setDownloadingTop5(false);
    }
  };

  const handleDownloadTop12 = async () => {
    const el = top12Ref.current;
    if (!el) return;
    setDownloadingTop12(true);
    try {
      const blob = await capturePosterElement(el);
      downloadBlob(blob, top12PosterFilename({ level, part, year, groupLabel }));
    } catch (err) {
      alert(`Could not save Top 12 poster: ${err.message || err}`);
    } finally {
      setDownloadingTop12(false);
    }
  };

  const handleDownloadTop20 = async () => {
    const el = top20Ref.current;
    if (!el) return;
    setDownloadingTop20(true);
    try {
      const blob = await capturePosterElement(el);
      downloadBlob(blob, top20PosterFilename({ level, part, year, groupLabel }));
    } catch (err) {
      alert(`Could not save Top 20 poster: ${err.message || err}`);
    } finally {
      setDownloadingTop20(false);
    }
  };

  const handleDownloadFlyer = async () => {
    const el = flyerRef.current;
    if (!el) return;
    setDownloadingFlyer(true);
    try {
      const blob = await capturePosterElement(el);
      downloadBlob(blob, flyerPosterFilename({ level, part, year, groupLabel }));
    } catch (err) {
      alert(`Could not save flyer: ${err.message || err}`);
    } finally {
      setDownloadingFlyer(false);
    }
  };

  const handleDownloadFlyerPdf = async () => {
    const el = flyerRef.current;
    if (!el) return;
    setDownloadingFlyerPdf(true);
    try {
      await exportFlyerPdf(el, flyerPdfFilename({ level, part, year, groupLabel }));
    } catch (err) {
      alert(`Could not save flyer PDF: ${err.message || err}`);
    } finally {
      setDownloadingFlyerPdf(false);
    }
  };

  const extraPosters = (flyer80.length ? 1 : 0)
    + (top5.length ? 1 : 0)
    + (top12.length ? 1 : 0)
    + (top20.length ? 1 : 0);
  const busy = downloadingAll || downloadingId != null || downloadingFlyer || downloadingFlyerPdf;

  const handleDownloadAll = async () => {
    if (!candidates.length && !top5.length && !top12.length && !top20.length && !flyer80.length) return;
    setDownloadingAll(true);
    try {
      if (flyerRef.current && flyer80.length) {
        const blob = await capturePosterElement(flyerRef.current);
        downloadBlob(blob, flyerPosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (top5Ref.current && top5.length) {
        const blob = await capturePosterElement(top5Ref.current);
        downloadBlob(blob, top5PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (top12Ref.current && top12.length) {
        const blob = await capturePosterElement(top12Ref.current);
        downloadBlob(blob, top12PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (top20Ref.current && top20.length) {
        const blob = await capturePosterElement(top20Ref.current);
        downloadBlob(blob, top20PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      for (const row of candidates) {
        const el = posterRefs.current.get(row.id);
        if (!el) continue;
        const blob = await capturePosterElement(el);
        downloadBlob(blob, posterFilename(row));
        await new Promise((r) => setTimeout(r, 350));
      }
    } catch (err) {
      console.error('[BoardResultsPosters] download all', err);
      alert(`Download stopped: ${err.message || err}`);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSelected = async () => {
    if (!selectedCount) return;
    setDownloadingAll(true);
    try {
      if (selectedKeys.has(SEL_FLYER) && flyerRef.current && flyer80.length) {
        const blob = await capturePosterElement(flyerRef.current);
        downloadBlob(blob, flyerPosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (selectedKeys.has(SEL_TOP5) && top5Ref.current && top5.length) {
        const blob = await capturePosterElement(top5Ref.current);
        downloadBlob(blob, top5PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (selectedKeys.has(SEL_TOP12) && top12Ref.current && top12.length) {
        const blob = await capturePosterElement(top12Ref.current);
        downloadBlob(blob, top12PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      if (selectedKeys.has(SEL_TOP20) && top20Ref.current && top20.length) {
        const blob = await capturePosterElement(top20Ref.current);
        downloadBlob(blob, top20PosterFilename({ level, part, year, groupLabel }));
        await new Promise((r) => setTimeout(r, 350));
      }
      for (const row of candidates) {
        if (!selectedKeys.has(studentSelKey(row.id))) continue;
        const el = posterRefs.current.get(row.id);
        if (!el) continue;
        const blob = await capturePosterElement(el);
        downloadBlob(blob, posterFilename(row));
        await new Promise((r) => setTimeout(r, 350));
      }
    } catch (err) {
      console.error('[BoardResultsPosters] download selected', err);
      alert(`Download stopped: ${err.message || err}`);
    } finally {
      setDownloadingAll(false);
    }
  };

  if (listLoading) {
    return (
      <div className="br-posters-empty">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading results…
      </div>
    );
  }

  if (candidates.length === 0 && top5.length === 0 && top12.length === 0 && top20.length === 0 && flyer80.length === 0) {
    return (
      <div className="br-posters-empty">
        <FontAwesomeIcon icon={faImage} className="br-posters-empty-icon" />
        <p>No students at {MIN_PCT}% or above in this view.</p>
        <p className="muted">
          {sectionLabel ? `${sectionLabel} · ` : ''}
          {groupLabel} · {level} {part} {year}
        </p>
      </div>
    );
  }

  return (
    <div className="br-posters">
      <input
        ref={replaceFileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/jpg,.jpg,.jpeg,.png,.webp"
        hidden
        onChange={handleReplacePhotoFile}
      />
      <div className="br-posters-toolbar">
        <div className="br-posters-toolbar-left">
          <strong>
            {candidates.length} poster{candidates.length === 1 ? '' : 's'}
            {flyer80.length ? ' + Flyer 21×8ft' : ''}
            {top5.length ? ' + Top 5' : ''}
            {top12.length ? ' + Top 12' : ''}
            {top20.length ? ' + Top 20' : ''}
          </strong>
          <span className="muted">
            ≥{MIN_PCT}%
            {sectionLabel ? ` · ${sectionLabel}` : ''}
            {photosLoading && photoLoadProgress.total > 0
              ? ` · loading photos (${photoLoadProgress.done}/${photoLoadProgress.total})…`
              : photosLoading
                ? ' · loading photos…'
                : ''}
          </span>
        </div>
        <div className="br-posters-toolbar-actions">
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            disabled={busy || allSelectableKeys.length === 0}
            onClick={allSelected ? clearSelection : selectAllPosters}
            title={allSelected ? 'Clear selection' : 'Select all posters'}
          >
            {allSelected ? 'Clear' : 'Select all'}
          </button>
          <button
            type="button"
            className="board-results-btn primary board-results-btn-sm"
            disabled={busy || selectedCount === 0}
            onClick={handleDownloadSelected}
            title="Download only selected posters"
          >
            <FontAwesomeIcon icon={downloadingAll && selectedCount ? faSpinner : faDownload} spin={downloadingAll && selectedCount > 0} />
            {downloadingAll && selectedCount > 0
              ? ' Downloading…'
              : ` Download selected (${selectedCount})`}
          </button>
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            disabled={busy}
            onClick={() => setShowVideo(true)}
            title="Play or download a video of student posters (1.5 seconds each)"
          >
            <FontAwesomeIcon icon={faVideo} /> Video
          </button>
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            disabled={busy}
            onClick={handleDownloadAll}
          >
            <FontAwesomeIcon icon={downloadingAll && !selectedCount ? faSpinner : faDownload} spin={downloadingAll && !selectedCount} />
            {downloadingAll && !selectedCount ? ' Downloading…' : ` Download all (${candidates.length + extraPosters})`}
          </button>
        </div>
      </div>

      <div className="br-posters-grid">
        {flyer80.length > 0 && (
          <div className={`br-poster-card-wrap is-flyer${selectedKeys.has(SEL_FLYER) ? ' is-selected' : ''}${adjustingFlyerPhotos ? ' is-adjusting-photos' : ''}`}>
            <label className="br-poster-select">
              <input
                type="checkbox"
                checked={selectedKeys.has(SEL_FLYER)}
                onChange={() => toggleSelected(SEL_FLYER)}
                aria-label="Select 21×8 ft flyer poster"
              />
            </label>
            <div
              role="button"
              tabIndex={0}
              className="br-poster-thumb is-flyer-thumb"
              ref={flyerThumbFrameRef}
              onClick={() => {
                if (adjustingFlyerPhotos) return;
                setPreviewIndex(null);
                setPreviewTop5(false);
                setPreviewTop12(false);
                setPreviewTop20(false);
                setPreviewFlyer(true);
              }}
              onKeyDown={(e) => {
                if (adjustingFlyerPhotos) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPreviewIndex(null);
                  setPreviewTop5(false);
                  setPreviewTop12(false);
                  setPreviewTop20(false);
                  setPreviewFlyer(true);
                }
              }}
              aria-label={adjustingFlyerPhotos ? 'Drag a student to move · click for fine tune' : 'Open flyer preview'}
            >
              <div
                className="br-poster-preview-wrap is-flyer-scaled"
                style={{ transform: `scale(${flyerThumbScale || 0.2})` }}
              >
                <BoardResultsFlyerPoster
                  ref={flyerRef}
                  rows={flyer80}
                  photoUrls={photoUrls}
                  photoPlacements={photoPlacements}
                  editingPhotos={adjustingFlyerPhotos}
                  editDragScale={flyerThumbScale || 0.2}
                  onPhotoClick={handleFlyerPhotoClick}
                  onPlacementChange={handleFlyerPlacementChange}
                  level={level}
                  part={part}
                  year={year}
                  sectionLabel={sectionLabel}
                  groupLabel={groupLabel}
                  theme={flyerTheme}
                />
              </div>
            </div>
            <div className="br-poster-card-meta">
              <span className="br-poster-card-name">Flyer 21 ft × 8 ft</span>
              <span className="br-poster-card-pct">
                ≥{FLYER_MIN_MARKS} marks · {flyer80.length} students
                {adjustingFlyerPhotos ? ' · drag student to move · click to fine-tune' : ''}
              </span>
            </div>
            <div className="br-flyer-theme-picker" role="group" aria-label="Flyer color theme">
              {FLYER_THEMES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`br-flyer-theme-swatch${flyerTheme === t.key ? ' is-active' : ''}`}
                  title={t.label}
                  aria-label={t.label}
                  aria-pressed={flyerTheme === t.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFlyerThemeAndSave(t.key);
                  }}
                >
                  <span style={{ background: t.swatches[0] }} />
                  <span style={{ background: t.swatches[1] }} />
                  <span style={{ background: t.swatches[2] }} />
                  <span style={{ background: t.swatches[3] }} />
                </button>
              ))}
              <span className="br-flyer-theme-label">
                {FLYER_THEMES.find((t) => t.key === flyerTheme)?.label || 'Theme'}
              </span>
            </div>
            <div className="br-poster-card-actions">
              <button
                type="button"
                className={`board-results-btn ${adjustingFlyerPhotos ? 'primary' : 'secondary'} board-results-btn-sm board-results-btn-icon`}
                onClick={() => setAdjustingFlyerPhotos((v) => !v)}
                title={adjustingFlyerPhotos ? 'Done adjusting' : 'Adjust student placement'}
                aria-pressed={adjustingFlyerPhotos}
              >
                <FontAwesomeIcon icon={faCrop} />
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm board-results-btn-icon"
                onClick={() => {
                  setPreviewIndex(null);
                  setPreviewTop5(false);
                  setPreviewTop12(false);
                  setPreviewTop20(false);
                  setPreviewFlyer(true);
                }}
                title="Preview flyer"
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm board-results-btn-icon"
                disabled={busy}
                onClick={handleDownloadFlyer}
                title="Download flyer PNG"
              >
                <FontAwesomeIcon icon={downloadingFlyer ? faSpinner : faDownload} spin={downloadingFlyer} />
              </button>
              <button
                type="button"
                className="board-results-btn primary board-results-btn-sm board-results-btn-icon"
                disabled={busy}
                onClick={handleDownloadFlyerPdf}
                title="Download high-quality PDF (21×8 ft print)"
              >
                <FontAwesomeIcon icon={downloadingFlyerPdf ? faSpinner : faFilePdf} spin={downloadingFlyerPdf} />
              </button>
            </div>
          </div>
        )}
        {top5.length > 0 && (
          <div className={`br-poster-card-wrap is-top5${selectedKeys.has(SEL_TOP5) ? ' is-selected' : ''}`}>
            <label className="br-poster-select">
              <input
                type="checkbox"
                checked={selectedKeys.has(SEL_TOP5)}
                onChange={() => toggleSelected(SEL_TOP5)}
                aria-label="Select Top 5 poster"
              />
            </label>
            <button
              type="button"
              className="br-poster-thumb"
              onClick={() => {
                setPreviewIndex(null);
                setPreviewTop12(false);
                setPreviewTop20(false);
                setPreviewFlyer(false);
                setPreviewTop5(true);
              }}
              title="Preview Top 5 poster"
            >
              <div
                className="br-poster-preview-wrap is-scaled"
                style={{ '--br-poster-scale': '0.3' }}
              >
                <BoardResultsTop5Poster
                  ref={top5Ref}
                  rows={top5}
                  photoUrls={photoUrls}
                  level={level}
                  part={part}
                  year={year}
                  groupLabel={groupLabel}
                  sectionLabel={sectionLabel}
                />
              </div>
            </button>
            <div className="br-poster-card-meta">
              <span className="br-poster-card-name">Top 5 Positions</span>
              <span className="br-poster-card-pct">{top5.length} students</span>
            </div>
            <div className="br-poster-card-actions">
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => {
                  setPreviewIndex(null);
                  setPreviewTop12(false);
                  setPreviewTop20(false);
                  setPreviewFlyer(false);
                  setPreviewTop5(true);
                }}
                title="Preview Top 5 poster"
              >
                <FontAwesomeIcon icon={faEye} /> Preview
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                disabled={downloadingAll || downloadingTop5}
                onClick={handleDownloadTop5}
                title="Download Top 5 PNG"
              >
                <FontAwesomeIcon
                  icon={downloadingTop5 ? faSpinner : faDownload}
                  spin={downloadingTop5}
                />
                PNG
              </button>
            </div>
          </div>
        )}
        {top12.length > 0 && (
          <div className={`br-poster-card-wrap is-top12${selectedKeys.has(SEL_TOP12) ? ' is-selected' : ''}`}>
            <label className="br-poster-select">
              <input
                type="checkbox"
                checked={selectedKeys.has(SEL_TOP12)}
                onChange={() => toggleSelected(SEL_TOP12)}
                aria-label="Select Top 12 poster"
              />
            </label>
            <button
              type="button"
              className="br-poster-thumb"
              onClick={() => {
                setPreviewIndex(null);
                setPreviewTop5(false);
                setPreviewTop20(false);
                setPreviewFlyer(false);
                setPreviewTop12(true);
              }}
              title="Preview Top 12 poster"
            >
              <div
                className="br-poster-preview-wrap is-scaled"
                style={{ '--br-poster-scale': '0.3' }}
              >
                <BoardResultsTop5Poster
                  ref={top12Ref}
                  count={12}
                  rows={top12}
                  photoUrls={photoUrls}
                  level={level}
                  part={part}
                  year={year}
                />
              </div>
            </button>
            <div className="br-poster-card-meta">
              <span className="br-poster-card-name">Top 12 Positions</span>
              <span className="br-poster-card-pct">{top12.length} students</span>
            </div>
            <div className="br-poster-card-actions">
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => {
                  setPreviewIndex(null);
                  setPreviewTop5(false);
                  setPreviewTop20(false);
                  setPreviewFlyer(false);
                  setPreviewTop12(true);
                }}
                title="Preview Top 12 poster"
              >
                <FontAwesomeIcon icon={faEye} /> Preview
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                disabled={downloadingAll || downloadingTop12}
                onClick={handleDownloadTop12}
                title="Download Top 12 PNG"
              >
                <FontAwesomeIcon
                  icon={downloadingTop12 ? faSpinner : faDownload}
                  spin={downloadingTop12}
                />
                PNG
              </button>
            </div>
          </div>
        )}
        {top20.length > 0 && (
          <div className={`br-poster-card-wrap is-top20${selectedKeys.has(SEL_TOP20) ? ' is-selected' : ''}`}>
            <label className="br-poster-select">
              <input
                type="checkbox"
                checked={selectedKeys.has(SEL_TOP20)}
                onChange={() => toggleSelected(SEL_TOP20)}
                aria-label="Select Top 20 poster"
              />
            </label>
            <button
              type="button"
              className="br-poster-thumb"
              onClick={() => {
                setPreviewIndex(null);
                setPreviewTop5(false);
                setPreviewTop12(false);
                setPreviewFlyer(false);
                setPreviewTop20(true);
              }}
              title="Preview Top 20 poster"
            >
              <div
                className="br-poster-preview-wrap is-scaled"
                style={{ '--br-poster-scale': '0.3' }}
              >
                <BoardResultsTop5Poster
                  ref={top20Ref}
                  count={20}
                  rows={top20}
                  photoUrls={photoUrls}
                  level={level}
                  part={part}
                  year={year}
                />
              </div>
            </button>
            <div className="br-poster-card-meta">
              <span className="br-poster-card-name">Top 20 Positions</span>
              <span className="br-poster-card-pct">{top20.length} students</span>
            </div>
            <div className="br-poster-card-actions">
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => {
                  setPreviewIndex(null);
                  setPreviewTop5(false);
                  setPreviewTop12(false);
                  setPreviewFlyer(false);
                  setPreviewTop20(true);
                }}
                title="Preview Top 20 poster"
              >
                <FontAwesomeIcon icon={faEye} /> Preview
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                disabled={downloadingAll || downloadingTop20}
                onClick={handleDownloadTop20}
                title="Download Top 20 PNG"
              >
                <FontAwesomeIcon
                  icon={downloadingTop20 ? faSpinner : faDownload}
                  spin={downloadingTop20}
                />
                PNG
              </button>
            </div>
          </div>
        )}
        {candidates.map((row, idx) => {
          const selKey = studentSelKey(row.id);
          const isSelected = selectedKeys.has(selKey);
          return (
          <div
            key={row.id || row.roll_no}
            className={`br-poster-card-wrap${isSelected ? ' is-selected' : ''}`}
          >
            <label className="br-poster-select">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelected(selKey)}
                aria-label={`Select poster for ${row.student_name || row.roll_no}`}
              />
            </label>
            <button
              type="button"
              className="br-poster-thumb"
              onClick={() => {
                setPreviewTop5(false);
                setPreviewTop12(false);
                setPreviewTop20(false);
                setPreviewFlyer(false);
                setPreviewIndex(idx);
              }}
              title="Preview poster"
            >
              <div
                className="br-poster-preview-wrap is-scaled"
                style={{ '--br-poster-scale': '0.3' }}
              >
                <BoardResultsPosterCard
                  ref={(el) => setPosterRef(row.id, el)}
                  row={row}
                  photoUrl={photoUrls[row.id]}
                />
              </div>
            </button>
            <div className="br-poster-card-meta">
              <span className="br-poster-card-name" title={row.student_name}>
                #{row.rank_no} {row.student_name}
              </span>
              <span className="br-poster-card-pct">{row.percentage_label || `${rowPct(row)}%`}</span>
            </div>
            <div className="br-poster-card-actions">
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm"
                onClick={() => {
                setPreviewTop5(false);
                setPreviewTop12(false);
                setPreviewTop20(false);
                setPreviewFlyer(false);
                setPreviewIndex(idx);
              }}
                title="Preview full-size poster"
              >
                <FontAwesomeIcon icon={faEye} /> Preview
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm board-results-btn-icon"
                disabled={replacingId === row.id || downloadingAll}
                onClick={() => handleReplacePhotoPick(row)}
                title="Replace student photo"
                aria-label="Replace student photo"
              >
                <FontAwesomeIcon
                  icon={replacingId === row.id ? faSpinner : faCamera}
                  spin={replacingId === row.id}
                />
              </button>
              <button
                type="button"
                className="board-results-btn secondary board-results-btn-sm board-results-btn-icon"
                disabled={downloadingAll || downloadingId === row.id}
                onClick={() => handleDownloadOne(row)}
                title="Download PNG for social media"
                aria-label="Download PNG for social media"
              >
                <FontAwesomeIcon
                  icon={downloadingId === row.id ? faSpinner : faDownload}
                  spin={downloadingId === row.id}
                />
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {previewIndex != null && candidates[previewIndex] && (
        <BoardResultsPosterPreview
          row={candidates[previewIndex]}
          photoUrl={photoUrls[candidates[previewIndex].id]}
          apiBaseUrl={apiBaseUrl}
          authHeaders={authHeaders}
          onClose={() => setPreviewIndex(null)}
          hasPrev={previewIndex > 0}
          hasNext={previewIndex < candidates.length - 1}
          onPrev={() => setPreviewIndex((i) => (i > 0 ? i - 1 : i))}
          onNext={() => setPreviewIndex((i) => (i < candidates.length - 1 ? i + 1 : i))}
          onPhotoReplaced={handlePhotoReplaced}
        />
      )}

      {previewTop5 && top5.length > 0 && (
        <BoardResultsPosterPreview
          customCard={(
            <BoardResultsTop5Poster
              rows={top5}
              photoUrls={photoUrls}
              level={level}
              part={part}
              year={year}
            />
          )}
          hideReplace
          title="Top 5 Positions"
          subtitle={top5Meta}
          downloadName={top5PosterFilename({ level, part, year, groupLabel })}
          onClose={() => setPreviewTop5(false)}
        />
      )}

      {previewTop12 && top12.length > 0 && (
        <BoardResultsPosterPreview
          customCard={(
            <BoardResultsTop5Poster
              count={12}
              rows={top12}
              photoUrls={photoUrls}
              level={level}
              part={part}
              year={year}
            />
          )}
          hideReplace
          title="Top 12 Positions"
          subtitle={top5Meta}
          downloadName={top12PosterFilename({ level, part, year, groupLabel })}
          onClose={() => setPreviewTop12(false)}
        />
      )}

      {previewTop20 && top20.length > 0 && (
        <BoardResultsPosterPreview
          customCard={(
            <BoardResultsTop5Poster
              count={20}
              rows={top20}
              photoUrls={photoUrls}
              level={level}
              part={part}
              year={year}
            />
          )}
          hideReplace
          title="Top 20 Positions"
          subtitle={top5Meta}
          downloadName={top20PosterFilename({ level, part, year, groupLabel })}
          onClose={() => setPreviewTop20(false)}
        />
      )}

      {previewFlyer && flyer80.length > 0 && (
        <BoardResultsPosterPreview
          layout="flyer"
          hideReplace
          title={`Flyer 21 ft × 8 ft · ≥${FLYER_MIN_MARKS} marks`}
          subtitle={`${flyer80.length} students · ${FLYER_THEMES.find((t) => t.key === flyerTheme)?.label || ''} · ${top5Meta}`}
          downloadName={flyerPosterFilename({ level, part, year, groupLabel })}
          pdfDownloadName={flyerPdfFilename({ level, part, year, groupLabel })}
          getCaptureElement={getFlyerCaptureElement}
          captureKey={`${flyerTheme}:${flyer80.length}:${flyer80.map((r) => r.id).join(',')}:${JSON.stringify(photoPlacements)}`}
          onClose={() => setPreviewFlyer(false)}
        />
      )}

      {editingPhotoRow && (
        <BoardResultsFlyerPhotoEditor
          row={editingPhotoRow}
          photoUrl={photoUrls[editingPhotoRow.id]}
          placement={photoPlacements[editingPhotoRow.id]}
          onSave={handleSavePhotoPlacement}
          onClose={() => setEditingPhotoRow(null)}
        />
      )}

      {showVideo && (
        <BoardResultsPosterVideo
          posterElements={candidates
            .map((row) => posterRefs.current.get(row.id))
            .filter(Boolean)}
          level={level}
          part={part}
          year={year}
          groupLabel={groupLabel}
          sectionLabel={sectionLabel}
          onClose={() => setShowVideo(false)}
        />
      )}
    </div>
  );
}
